export enum ProjectTag {
  RebuildManufacturing = 'Re:Build Manufacturing',
  Personal = 'Personal',
  FugueLab = 'Fugue Lab',
}

export enum ServiceType {
  WebDevelopment = 'webDevelopment',
  GraphicDesign = 'graphicDesign',
}

export enum ClientType {
  Soloist = 'Soloist',
  Vocalist = 'Vocalist',
  Ensemble = 'Ensemble',
  Quartet = 'Quartet',
  Church = 'Church',
  Festival = 'Festival',
  Organization = 'Organization',
  Publication = 'Publication',
  Coach = 'Coach',
  Educator = 'Educator',
  University = 'University',
  School = 'School',
  Venue = 'Venue',
  Composer = 'Composer',
  Conductor = 'Conductor',
  ArtsOrganization = 'Arts Organization',
  NonProfit = 'Non-Profit',
  MusicSchool = 'Music School',
  Conservatory = 'Conservatory',
  PerformingArtsCenter = 'Performing Arts Center',
  Organist = 'Organist',
}

export interface ProjectMetric {
  label: string
  value: string
  description?: string
}

export interface ProjectTestimonial {
  quote: string
  author: string
  role: string
}

export interface Project {
  slug: string
  title: string
  description: string
  image: string
  images?: string[] // Gallery images
  beforeImage?: string // For before/after comparisons
  url: string
  tags: ProjectTag[]
  serviceTypes?: ServiceType[]
  clientTypes?: ClientType[] // Enhanced filtering (array for multiple categories)
  tech: string[]
  client: string
  summary: string
  objectives: string[]
  implementation: string[]
  results: string[]
  metrics?: ProjectMetric[] // Quantifiable results
  testimonial?: ProjectTestimonial // Client testimonial
}

export const projects: Project[] = [
  {
    slug: 'chroma-music-festival',
    title: 'Chroma International Music Festival',
    description:
      'Website for a new international music festival launching in Hungary, featuring programs for piano and strings. Built for early credibility, participant info, and long-term growth.',
    image: '/portfolio/chroma-festival.png',
    images: undefined,
    url: 'https://chromafestival.vercel.app',
    tags: [ProjectTag.FugueLab],
    serviceTypes: [ServiceType.WebDevelopment],
    clientTypes: [ClientType.Festival],
    tech: ['Next.js', 'Tailwind CSS', 'ShadCN UI', 'Vercel', 'Terraform'],
    client: 'International classical music festival in Miskolc, Hungary',
    summary:
      'The festival organizers needed a polished online presence to attract students, faculty, and donors ahead of their inaugural summer season.',
    objectives: [
      "Design a compelling visual identity to reflect the festival's international caliber",
      'Clearly communicate dates, faculty, and programs for piano and strings',
      'Lay the foundation for future growth, donations, and application systems',
    ],
    implementation: [
      'Built a fast-loading, mobile-first site with elegant typography and visuals',
      'Structured content to support clear program info and faculty bios',
      'Included a mailing list sign-up and optional pre-registration interest form',
      'Optimized for SEO and social sharing to boost visibility pre-launch',
    ],
    results: [
      'Established credibility with students and artistic faculty during planning stage',
      'Enabled early marketing and promotional efforts across platforms',
      'Built a foundation for application processing and future e-commerce integration',
    ],
    metrics: [
      {
        label: 'Page Load Time',
        value: '<1s',
        description: 'Optimized for international audiences',
      },
      {
        label: 'Mobile Score',
        value: '95+',
        description: 'Google Lighthouse performance',
      },
    ],
  },
  {
    slug: 'chroma-logo-concepts',
    title: 'Chroma Festival — Logo Exploration',
    description:
      'Brand identity exploration for an international music festival. These concept designs feature an 11-color brandmark drawn from the twelve-tone chromatic scale, connecting the festival name "Chroma" with the colors of music itself.',
    image: '/portfolio/graphic-design/chroma-03.png',
    images: undefined,
    url: '',
    tags: [ProjectTag.FugueLab],
    serviceTypes: [ServiceType.GraphicDesign],
    clientTypes: [ClientType.Festival],
    tech: ['Adobe Illustrator', 'Brand Identity Design', 'Logo Design'],
    client: 'International classical music festival in Miskolc, Hungary',
    summary:
      'The festival wanted to explore logo concepts that would capture their unique mission of bringing color and vibrancy to classical music education.',
    objectives: [
      'Create a memorable logo connecting the concept of "chroma" (color) with music theory',
      'Design a flexible brand system that works across digital and print applications',
      'Establish a professional, international presence that appeals to students and faculty',
    ],
    implementation: [
      'Developed a unique 11-color brandmark representing the 11 chromatic notes',
      'Created multiple logo variations for different use cases and backgrounds',
      'Designed with scalability in mind for both large-format print and small digital icons',
      'Explored color palettes and typography that reflect both artistry and professionalism',
    ],
    results: [
      'Delivered multiple concept directions showcasing the chromatic color-music connection',
      'Created a distinctive visual approach that stands out in the festival landscape',
      'Demonstrated how design can tell a musical story at first glance',
    ],
  },
  {
    slug: 'classically-minded',
    title: 'Classically Minded',
    description:
      'A blog-style digital publication dedicated to classical music thought leadership. Built for long-term SEO, writer-friendly content workflows, and clean, reader-focused design.',
    image: '/portfolio/classically-minded.png',
    url: 'https://classicallyminded.org',
    tags: [ProjectTag.FugueLab],
    serviceTypes: [ServiceType.WebDevelopment],
    clientTypes: [ClientType.Publication],
    tech: ['Squarespace'],
    client: 'Independent publication by classical music advocates',
    summary:
      'Classically Minded wanted to create a serious platform for long-form writing and artistic thought. The site required clarity, longevity, and tools for a growing contributor base.',
    objectives: [
      'Design a clean and timeless blog layout',
      'Support SEO growth with clean semantic markup',
      'Enable easy publishing by contributors without tech experience',
    ],
    implementation: [
      'Custom Squarespace blog configuration',
      'Mobile-first design with optimized image delivery',
      'Structured navigation and categories for discoverability',
    ],
    results: [
      'Improved site authority and search visibility',
      'Positive user feedback from readers and contributors',
      'Content management streamlined for non-technical authors',
    ],
  },
  {
    slug: 'holy-trinity-covina',
    title: 'Holy Trinity Covina – Music Ministry',
    description:
      'A parish site for an Episcopal church in LA County featuring music archives, choir schedules, giving integration, and custom event sections tailored to liturgical needs.',
    image: '/portfolio/holy-trinity.jpeg',
    url: 'https://holytrinitycovina.com',
    tags: [ProjectTag.FugueLab],
    serviceTypes: [ServiceType.WebDevelopment],
    clientTypes: [ClientType.Church],
    tech: ['Squarespace', 'Tithely Giving Platform'],
    client: 'Episcopal church in Covina, CA',
    summary:
      'Holy Trinity needed a better way to communicate with its congregation and showcase its rich music ministry.',
    objectives: [
      'Modernize the parish website',
      'Feature the choir and music program prominently',
      'Enable online giving and easier service updates',
    ],
    implementation: [
      'Liturgical calendar integration',
      'Tithely donation platform embed',
      'Archived and searchable choir music list with download links',
    ],
    results: ['Increased online donations', 'Streamlined choir communication', 'More engagement from local community'],
  },
  {
    slug: 'kaia-string-quartet',
    title: 'KAIA String Quartet',
    description:
      'Custom site for a vibrant, all-female Chicago-based string quartet. Features include concert listings, ensemble media, education services, and an embedded email list.',
    image: '/portfolio/kaia-string-quartet.jpg',
    url: 'https://kaiastringquartet.com',
    tags: [ProjectTag.FugueLab],
    serviceTypes: [ServiceType.WebDevelopment],
    clientTypes: [ClientType.Quartet, ClientType.Ensemble],
    tech: ['Squarespace', 'MailChimp', 'Vimeo/SoundCloud Embeds'],
    client: 'Professional string quartet with a Latin American focus',
    summary:
      'KAIA needed a site that could handle press inquiries, concert promotion, and educational outreach all in one.',
    objectives: [
      'Design a bold and feminine brand presence',
      'Highlight performances and media',
      'Grow their mailing list and concert bookings',
    ],
    implementation: [
      'Responsive Squarespace layout with custom graphics',
      'Concert calendar with ticket links',
      'Email marketing integration via MailChimp',
    ],
    results: [
      'Clearer messaging for bookers and fans',
      'Improved press and media exposure',
      'Steady mailing list growth',
    ],
  },
  {
    slug: 'jonathan-gregoire',
    title: 'Jonathan Gregoire – Creative Coach',
    description:
      'Sales-focused landing page for a creative coach and organist. Highlights include branding, lead capture, and targeted CTAs to convert visitors into clients.',
    image: '/portfolio/jonathan-gregoire.jpg',
    url: 'https://jonathangregoire.com',
    tags: [ProjectTag.FugueLab],
    serviceTypes: [ServiceType.WebDevelopment],
    clientTypes: [ClientType.Coach, ClientType.Soloist],
    tech: ['Next.js', 'ShadCN UI', 'Framer Motion', 'Vimeo Embeds'],
    client: 'Professional organist and creative coach',
    summary:
      'Jonathan needed a streamlined site to convert traffic into paying coaching clients while showcasing his artistic credibility.',
    objectives: [
      'Build a bold personal brand around creativity and coaching',
      'Generate leads through clear calls to action',
      'Present past work and testimonials persuasively',
    ],
    implementation: [
      'Animated components using Framer Motion',
      'Simple contact funnel with lead capture form',
      'Modern design system built on ShadCN UI',
    ],
    results: [
      'Increased email inquiries for coaching',
      'Boosted brand confidence and visual identity',
      'Stronger alignment between brand tone and audience expectations',
    ],
  },
  {
    slug: 'abigayle-flack',
    title: 'Abigayle Flack – Soprano',
    description:
      'Personal portfolio site for a professional soprano. Includes performance video samples, biography, and contact section for bookings.',
    image: '/portfolio/abigayle-flack.jpg',
    url: 'https://abigayleflack.com',
    tags: [ProjectTag.FugueLab],
    serviceTypes: [ServiceType.WebDevelopment],
    clientTypes: [ClientType.Soloist, ClientType.Vocalist],
    tech: ['Next.js', 'ShadCN UI', 'SoundCloud', 'Vimeo'],
    client: 'Professional soprano and performer',
    summary:
      'Abigayle needed a sleek digital portfolio to support auditions, concert promotion, and direct outreach by artistic directors.',
    objectives: [
      'Create a memorable, brand-aligned design',
      'Provide media access for directors and agents',
      'Support organic discovery and bookings',
    ],
    implementation: [
      'Custom components using ShadCN UI and Tailwind',
      'Performance media section with Vimeo and SoundCloud embeds',
      'Lightweight Next.js deployment for fast load times',
    ],
    results: [
      'Improved discoverability online',
      'Professional portfolio now referenced in applications',
      'Reduced friction for media playback across devices',
    ],
  },
  {
    slug: 'susan-bengtson-price',
    title: 'Dr. Susan Bengtson Price – Viola & Teaching',
    description:
      'Professional portfolio for a violist and music educator. Includes CV, media clips, teaching philosophy, and student testimonials.',
    image: '/portfolio/susan-bengtson.jpg',
    images: undefined,
    url: 'https://www.susanbengtson.com/',
    tags: [ProjectTag.FugueLab],
    serviceTypes: [ServiceType.WebDevelopment],
    clientTypes: [ClientType.Soloist, ClientType.Educator],
    tech: ['Next.js', 'ShadCN UI', 'Vimeo', 'Custom Forms'],
    client: 'College professor and private studio teacher',
    summary:
      'Susan wanted a modern website that could appeal to both concert presenters and prospective viola students.',
    objectives: [
      'Balance academic, artistic, and pedagogical audiences',
      'Make application and contact process simple',
      'Host media in a clean, attractive layout',
    ],
    implementation: [
      'Custom contact form for lesson inquiries',
      'Multimedia player embeds for video and audio',
      'CV and teaching philosophy pages for credibility',
    ],
    results: [
      'Increased lesson inquiries from students',
      'Streamlined audition prep communications',
      'Improved site professionalism during grant applications',
    ],
    metrics: [
      {
        label: 'Lesson Inquiries',
        value: '3x',
        description: 'Increase in student applications',
      },
    ],
    testimonial: {
      quote:
        "I highly recommend enlisting Garrett's expertise to design (or redesign) your website... he creates a product even better than you could have imagined on your own.",
      author: 'Dr. Susan Bengtson Price',
      role: 'Violist & Educator',
    },
  },
  {
    slug: 'matt-mueller',
    title: 'Matt Mueller – Baritone',
    description:
      'Clean and elegant site for a baritone singer, featuring performance media, repertoire list, and professional biography.',
    image: '/portfolio/matt-mueller.png',
    images: undefined,
    url: 'https://www.mattmmueller.com/',
    tags: [ProjectTag.FugueLab],
    serviceTypes: [ServiceType.WebDevelopment],
    clientTypes: [ClientType.Soloist, ClientType.Vocalist],
    tech: ['Next.js', 'ShadCN UI', 'SoundCloud', 'Vimeo'],
    client: 'Baritone singer and recitalist',
    summary: 'Matt needed a clean, professional site to support his career goals in opera and recital performance.',
    objectives: [
      'Highlight vocal performance credentials',
      'Offer a streamlined, mobile-friendly UX',
      'Create a permanent archive for his media and materials',
    ],
    implementation: [
      'Tailored mobile layouts using responsive design',
      'Custom media section organized by repertoire',
      'Typography and layout optimized for clarity and elegance',
    ],
    results: [
      'Site praised by directors and agents',
      'Easier submission process for professional opportunities',
      'Boosted confidence in his digital presence',
    ],
    testimonial: {
      quote:
        "My experience working with Garrett was a pleasure from start to finish. He was organized, efficient and highly professional in helping to realize my vision for my website... Can't recommend him highly enough.",
      author: 'Matt Mueller',
      role: 'Baritone',
    },
  },
  {
    slug: 'david-heinze',
    title: 'David Heinze – Organist',
    description:
      'Digital calling card for a professional organist. Includes audio/video samples, upcoming recitals, and church music service information.',
    image: '/portfolio/david-heinze.jpg',
    url: 'https://davidsheinze.com',
    tags: [ProjectTag.FugueLab],
    serviceTypes: [ServiceType.WebDevelopment],
    clientTypes: [ClientType.Organist, ClientType.Soloist],
    tech: ['Next.js', 'ShadCN UI', 'YouTube', 'Mailing List Signup'],
    client: 'Liturgical organist and concert performer',
    summary:
      'David wanted a simple but sharp online presence to help supporters find his performances and connect with church audiences.',
    objectives: [
      'Share upcoming recital dates',
      'Allow easy video/audio preview',
      'Enable supporters to follow his work easily',
    ],
    implementation: [
      'Event calendar and upcoming services section',
      'Media embeds from YouTube and SoundCloud',
      'Email list integration with custom styling',
    ],
    results: [
      'Greater attendance at concerts',
      'Improved press materials for promoters',
      'Increased mailing list signups and engagement',
    ],
  },
  {
    slug: 'patrick-pope',
    title: 'Dr. Patrick Pope – Organist & Composer',
    description:
      'Comprehensive portfolio for a church musician and composer, showcasing compositions, recordings, and liturgical music credentials.',
    image: '/portfolio/patrick-pope.jpg',
    url: 'https://patrickpopeorganist.com',
    tags: [ProjectTag.FugueLab],
    serviceTypes: [ServiceType.WebDevelopment],
    tech: ['Next.js', 'ShadCN UI', 'SoundCloud', 'Vimeo'],
    client: 'Church musician and liturgical composer',
    summary:
      'Patrick needed a way to present both his performance and compositional work in a clear and dignified online format.',
    objectives: [
      'Highlight choral and liturgical compositions',
      'Present audio scores and concert media',
      'Build trust with churches, ensembles, and commissioners',
    ],
    implementation: [
      'Audio player and PDF score preview layout',
      'Composer and performer bio organization',
      'Responsive site with performance media highlight sections',
    ],
    results: [
      'More commission inquiries',
      'Wider dissemination of his works online',
      'Credible presence for liturgical organizations and grant panels',
    ],
    clientTypes: [ClientType.Organist],
  },
  {
    slug: 'music-garrett-john-law',
    title: 'Garrett John Law – Organist & Conductor',
    description:
      'A modern, personal website for a professional church musician. Built to showcase media, performances, credentials, and service information with elegance and clarity.',
    image: '/portfolio/garrett.webp',
    url: 'https://music.garrettjohnlaw.com',
    tags: [ProjectTag.FugueLab],
    serviceTypes: [ServiceType.WebDevelopment],
    tech: ['Next.js', 'Tailwind CSS', 'ShadCN', 'Payload CMS', 'Vercel'],
    client: 'Garrett John Law',
    summary:
      'As a working organist, conductor, and teacher, Garrett needed a web presence that reflected both artistic polish and practical details for churches, concert organizers, and students.',
    objectives: [
      'Showcase biographical and media content in an elegant, mobile-friendly layout',
      'Provide an up-to-date calendar, service details, and contact options',
      'Present professional credibility for church searches and performance inquiries',
    ],
    implementation: [
      'Custom-designed frontend using Tailwind CSS and fluid typography',
      'Payload CMS backend for easy content updates and media uploads',
      'Audio/video embeds, responsive galleries, and clean performance listings',
    ],
    results: [
      'Established a credible and polished online presence',
      'Improved visibility in church hiring and concert booking',
      'Flexible content management with modern development stack',
    ],
    clientTypes: [ClientType.Organist],
  },
  {
    slug: 'reva-ai-infrastructure',
    title: 'AI Chatbot Infrastructure',
    description:
      'Built the infrastructure for an AI-powered chatbot from scratch: Terraform for multi-environment deployment, observability for product and engineering, and CI/CD for automated deployments. Improved embedding speed by 20X and reduced team size from 6 to 2 developers.',
    image: '/portfolio/reva-ai.png',
    url: '',
    tags: [ProjectTag.RebuildManufacturing],
    tech: ['Terraform', 'AWS ECS', 'AWS Bedrock', 'LiteLLM', 'Open WebUI', 'CloudWatch', 'CI/CD', 'Python'],
    client: 'Re:Build Manufacturing',
    summary:
      'Re:Build needed an AI chatbot infrastructure that could support RAG, observability, and rapid iteration. I built the infrastructure from scratch and made key build-vs-buy decisions that dramatically reduced complexity and team size.',
    objectives: [
      'Build multi-environment infrastructure (dev, staging, prod) from scratch',
      'Make smart build-vs-buy decisions to reduce complexity',
      'Enable observability for product (usage) and engineering (performance)',
      'Identify and fix performance bottlenecks in the RAG pipeline',
    ],
    implementation: [
      'Wrote Terraform config for dev, staging, and prod environments',
      'Chose Open WebUI (open source) over building a custom frontend—saved hundreds of developer hours',
      'Built metrics and monitoring so product can see usage, developers can see performance',
      'Set up alarms for critical regressions',
      'Identified bottleneck: Bedrock Access Gateway was slow for RAG embeddings',
      'Replaced with LiteLLM + batched embeddings for 20X improvement in embedding speed',
      'Automated Docling image deployment to ECR with dynamic, repeatable CI/CD',
    ],
    results: [
      'Reduced team from 6 developers to 2 by choosing Open WebUI',
      'Shipped in weeks instead of months',
      '20X improvement in embedding speed',
      'Production-ready observability stack for product and engineering teams',
    ],
    metrics: [
      {
        label: 'Team Reduction',
        value: '6 → 2',
        description: 'Developers needed after Open WebUI decision',
      },
      {
        label: 'Embedding Speed',
        value: '20X faster',
        description: 'After LiteLLM + batching optimization',
      },
    ],
  },
  {
    slug: 'composite-resources-scheduler',
    title: 'Manufacturing Scheduler',
    description:
      'A finite capacity scheduling application built through daily customer collaboration. Simple design (spreadsheet in, spreadsheet out) delivered in 1 month—outperforming a vendor solution that took months and cost $30k/year.',
    image: '/portfolio/manufacturing-scheduler.png',
    url: '',
    tags: [ProjectTag.RebuildManufacturing],
    tech: ['.NET', 'Spreadsheet I/O', 'Finite Scheduling Algorithms'],
    client: 'Re:Build Manufacturing',
    summary:
      'The manufacturing team needed a finite scheduling solution. Through daily customer meetings, I built a simple executable that takes a spreadsheet and outputs a scheduled spreadsheet—production-ready in 1 month.',
    objectives: [
      'Understand complex scheduling requirements through direct customer communication',
      'Build a practical solution that fits existing workflows (spreadsheet-based)',
      'Deliver a production-ready tool faster than enterprise alternatives',
      'Handle finite capacity constraints for manufacturing operations',
    ],
    implementation: [
      'Held daily customer meetings to nail down requirements and business logic',
      'Designed a simple approach: executable that takes a spreadsheet, outputs a spreadsheet',
      'Built finite capacity scheduling algorithms to respect resource constraints',
      'Delivered production-ready solution in 1 month',
    ],
    results: [
      'Production-ready in 1 month vs. months for an outside vendor',
      'Exceeded feature parity with a vendor solution priced at $30k/year',
      'No complex on-prem deployment or massive compute required',
      'Demonstrated value of customer communication and practical engineering',
    ],
    metrics: [
      {
        label: 'Time to Production',
        value: '1 month',
        description: 'From kickoff to production-ready',
      },
      {
        label: 'Cost Savings',
        value: '$30k+/year',
        description: 'Compared to vendor pricing',
      },
    ],
  },
  {
    slug: 'journeyman-backend',
    title: 'Virtual Shop API & Tool Modeling SDK',
    description:
      'Backend services for a manufacturing intelligence platform that models CNC machining operations and predicts programming and machining time.',
    image: '/portfolio/journeyman-backend.png', // Optional
    url: '', // Internal-only
    tags: [ProjectTag.RebuildManufacturing],
    tech: [
      '.NET 8',
      'AWS Lambda',
      'Step Functions',
      'GraphQL (Hot Chocolate)',
      'DynamoDB',
      'XUnit',
      'FluentValidation',
      'AutoMapper',
      'NuGet SDKs',
    ],
    client: 'Re:Build Manufacturing',
    summary:
      'I was a core contributor to the backend of a quoting platform for CNC machining. The system takes 3D part models and calculates estimated machining and programming time using configurable toolpaths and machine shop rules. I designed and maintained key APIs, data models, and developer SDKs.',
    objectives: [
      'Model toolpaths, machines, and operations for quote-time accuracy',
      'Enable composable APIs for quoting and manufacturing workflows',
      'Provide reusable libraries for internal and external consumption',
      'Ensure high test coverage and scalable architecture',
    ],
    implementation: [
      'Designed and implemented polymorphic APIs for tool and operation types using GraphQL + .NET',
      'Owned DynamoDB data layer with manual pagination, key design, and attribute mapping',
      'Wrote NuGet SDKs for shared tooling (serialization, conversion, error handling)',
      'Built pipeline to analyze tool changes, DFM feedback, and machining activity logs',
      'Integrated AWS Step Functions and Lambdas for async task orchestration',
    ],
    results: [
      'Deployed stable backend used across quoting and internal systems',
      'Created reusable tooling adopted by multiple engineering teams',
      'Achieved >90% test coverage with XUnit, FluentValidation, and Moq',
      'Became SME for virtual shop modeling and CNC quoting data pipelines',
    ],
  },
  {
    slug: 'financial-reporting-api',
    title: 'Financial Reporting API & Consolidation Service',
    description:
      'A unified API layer for aggregating financial data from disparate ERP systems across Re:Build subsidiaries. Enabled consistent reporting, dashboards, and executive visibility.',
    image: '/portfolio/financial-reporting.png', // Optional: update if available
    url: '', // Internal-only project
    tags: [ProjectTag.RebuildManufacturing],
    tech: ['.NET', 'AWS Lambda', 'Service Workers', 'REST APIs', 'QuickBooks API', 'Deltek', 'JobBOSS'],
    client: 'Re:Build Manufacturing',
    summary:
      'Re:Build needed a single source of truth for financials across multiple business units, each using different ERP systems. I led the design and implementation of a unified financial reporting API that became a foundational backend service for executive dashboards and financial reporting tools.',
    objectives: [
      'Unify data access across ERP systems (Deltek, JobBOSS, QuickBooks)',
      'Enable secure on-prem communication with legacy financial systems',
      'Support accurate, consolidated financial reporting across subsidiaries',
      'Bridge engineering and financial analyst teams to ensure data accuracy',
    ],
    implementation: [
      'Led architecture and full-stack implementation of the reporting API in .NET',
      'Used AWS Lambda for secure, scalable data processing across regions',
      'Deployed service workers to access on-prem ERP systems without direct VPN tunnels',
      'Integrated QuickBooks Online API to pull financials from SMB entities',
      'Standardized and validated financial data into a unified schema',
      'Worked closely with financial analysts to verify metric accuracy and reporting structure',
    ],
    results: [
      'Enabled executive dashboards with trusted, consolidated financial data',
      'Reduced manual spreadsheet workflows and data reconciliation time',
      'Improved reporting consistency across divisions and ERP systems',
      'Deepened collaboration between software and finance teams across the org',
      'Expanded my own understanding of accounting, P&L structure, and business decision-making',
    ],
  },
  {
    slug: 'revision',
    title: 'CNC Parameter Version Control',
    description:
      'An on-premise Next.js app for managing and versioning machine configurations on the manufacturing floor. It captures real-time operator changes and supports engineering approval workflows.',
    image: '/portfolio/revision.png', // Optional if available
    url: '', // Internal-only
    tags: [ProjectTag.RebuildManufacturing],
    tech: ['Next.js', 'NextAuth', 'OPC UA', 'TailwindCSS', 'TypeScript', 'Node.js', 'Polling', 'Custom Diffing Engine'],
    client: 'Re:Build Manufacturing',
    summary:
      'Operators were modifying CNC machining parameters directly at the machine, leading to tribal knowledge and inconsistent setups. I built a version control system for manufacturing processes, enabling engineers to track, approve, and deploy configurations from their office.',
    objectives: [
      'Track and version machine configuration changes made by operators',
      'Enable engineers to manage and deploy configurations remotely',
      'Ensure auditability and rollback capability for mission-critical changes',
      'Integrate with existing machine communication protocols on-prem',
    ],
    implementation: [
      'Built a custom on-prem Next.js app deployed within the factory network',
      'Implemented secure authentication using NextAuth',
      'Established OPC UA communication with machines to read/write config data',
      'Created polling infrastructure to detect and diff changes made by operators',
      'Stored changes as versioned records for engineering review and audit trail',
      'Developed intuitive UI for deploying configurations and reviewing change history',
    ],
    results: [
      'Engineers can now deploy CNC configuration changes from their desks',
      'Operator-made changes are automatically captured and reviewed',
      'Reduced downtime and improved traceability for part setup adjustments',
      'Introduced DevOps-style version control to physical manufacturing processes',
      'Gained firsthand experience with industrial protocols and factory network constraints',
    ],
  },
  {
    slug: 'fugue-lab-leadgen-agents',
    title: 'Autonomous Lead-Gen Agent Fleet',
    description:
      'An autonomous lead-generation system of ~15 LLM agents (plus a few supporting Lambdas) on AWS Lambda and Bedrock. Discovers prospects daily, drafts grounded outreach via SES behind an evidence-auditor safety gate, and runs a self-improving loop that rewrites its own strategy every week based on what converts.',
    image: '',
    url: '',
    tags: [ProjectTag.Personal],
    tech: ['AWS Bedrock', 'Claude Sonnet + Haiku', 'AWS Lambda', 'DynamoDB', 'SES', 'Terraform', 'TypeScript'],
    client: 'Fugue Lab (internal)',
    summary:
      'Fugue Lab needed a repeatable, automated way to fill a consulting pipeline without spending hours each week prospecting — and without naive LLM outreach hallucinating criticism of a prospect’s site. I built a three-layer multi-agent system (sensors → synthesizers → dispatchers) that discovers leads, drafts grounded outreach behind a safety gate, and rewrites its own strategy weekly.',
    objectives: [
      'Discover and score prospects daily across many metros and org types',
      'Send grounded, personalized outreach with zero unsupported claims',
      'Capture full-funnel analytics (SES open/click, GA4 sessions, Calendly bookings)',
      'Build a self-improving loop where the optimizer rewrites strategy weekly',
      'Keep Bedrock cost low enough to run unattended against a small MRR',
    ],
    implementation: [
      'Architected as three layers: Sensors (discovery, analytics, event-enrichment), Synthesizers (weekly strategy optimizer, daily pulse, weekly report), and Dispatchers (outreach, content, demo dispatcher)',
      'Built a grounding gate in front of every send — a Claude Haiku evidence-auditor classifies each draft against its research, surgically repairs one offending clause, or HOLDS it (four blocking checks: unsupported-defect, premise-contradiction, diagnostic-subject, unsolicited-criticism)',
      'Strategy optimizer reads ~90 days of conversion history and uses Bedrock tool-use to emit a fresh strategy brief each Sunday; discovery and outreach read it at startup so targeting self-adjusts',
      'Split work across model tiers (Haiku for scoring/validation, Sonnet for real writing) and added a 72-hour re-draft dedup window so held leads aren’t re-billed every run',
      'Deployed serverless with Terraform, DynamoDB single-table CRM, SES configuration sets, an S3 compare-and-set run-lock for concurrency, and GitHub Actions OIDC deploys to a leadgen/prod branch',
    ],
    results: [
      'Pipeline runs fully unattended Monday–Friday; Garrett only handles warm replies',
      'Diagnosing a self-contradicting drafter prompt removed the dominant cause of held drafts',
      'A $100→$50 monthly cap plus the dedup window roughly halved outreach spend',
      'Self-improving loop iterates strategy briefs week over week without manual tuning',
    ],
    metrics: [
      {
        label: 'LLM Agents',
        value: '~15',
        description: 'LLM agents in the pipeline, plus a few supporting Lambdas',
      },
      {
        label: 'Monthly Bedrock Cap',
        value: '$100→$50',
        description: 'Cap cut in config; observed outreach spend roughly halved',
      },
      {
        label: 'Strategy Cadence',
        value: 'Weekly',
        description: 'Optimizer rewrites targeting every Sunday',
      },
    ],
  },
  {
    slug: 'finboard-doctrine-agent',
    title: 'Operations Agent + Doctrine System',
    description:
      'A Bedrock Claude agent that runs a whole operation through 135 tools — bookkeeping across three entities, real-estate underwriting, CRM, calendar and domain workflows — plus a "doctrine" system that single-sources its domain rules across the agent prompt, the Claude Code skills, and the code itself, so they never drift.',
    image: '',
    url: '',
    tags: [ProjectTag.Personal],
    tech: ['AWS Bedrock', 'Claude Sonnet 4.6', 'AWS Lambda', 'DynamoDB', 'TypeScript', 'Zod'],
    client: 'Personal (finboard)',
    summary:
      'The same domain rules — expense routing, receipt matching, calendar routing — lived in three places at once (the agent prompt, a dozen skill docs, and the code) and silently drifted. And an agent that calls real tools can’t finish inside an API Gateway’s 30-second window. I solved both with a context-engineering system and an async agent loop.',
    objectives: [
      'Eliminate drift between the agent prompt, skill docs, and code',
      'Give the agent a large tool surface without trusting it to act unsupervised',
      'Run long multi-tool reasoning turns without hitting the 30s gateway cap',
      'Auto-link paid receipts to their ledger transactions, safely',
    ],
    implementation: [
      'Built a doctrine module where each domain contract is defined once; renderDoctrine([...]) composes the agent system prompt from those fragments and a generator stamps the same fragments into the skill docs',
      'Declared the full 135-tool surface in a shared registry — read tools auto-execute; write tools are surfaced to the model but intercepted and staged as reviewable plan-ops that commit only after human approval',
      'Beat the 30s gateway cap by having /chat/send self-invoke the Lambda asynchronously (InvocationType: Event) and run the agent loop off the request path with a 180s timeout while the UI polls status (up to 20 model turns)',
      'Added a daily receipt-to-transaction matcher: exact amount, ±7 days, unique match (vendor keyword tie-breaker), with the link enqueued by the canonical writer so the contract is code-enforced',
    ],
    results: [
      'Domain rules stopped drifting — there is structurally one copy',
      'The agent runs long tool-using turns without gateway timeouts',
      'Every write is proposed and human-approved before it touches the books',
      'Receipts link to transactions automatically and safely (unique-match only)',
    ],
    metrics: [
      {
        label: 'Agent Tools',
        value: '135',
        description: 'Read + write tools in one shared registry',
      },
      {
        label: 'Rule Sources',
        value: '1',
        description: 'Single source of truth for domain contracts',
      },
      {
        label: 'Async Worker',
        value: '180s',
        description: 'Beats the 30s API Gateway cap',
      },
    ],
  },
  {
    slug: 'full-score-quality-engine',
    title: 'The Full Score — Website Evaluation Engine',
    description:
      'A quality-grading engine that audits any live website across six weighted pillars and seven automated checks — including a browser-rendered mode that catches the text-over-image contrast and dead-CTA failures static checkers are blind to — and turns the gaps into a prioritized plan.',
    image: '',
    url: '',
    tags: [ProjectTag.Personal],
    tech: ['TypeScript', 'Playwright', 'axe-core', 'jsdom', 'Node CLI'],
    client: 'Fugue Lab (internal)',
    summary:
      'Most website audits grade one dimension and stop, and static accessibility checkers are blind to real failures — they can only compare text against a solid CSS background color, so light text over a busy hero image passes. Selling a rebuild needs a defensible, repeatable standard. I built an evaluation engine that produces one.',
    objectives: [
      'Grade a site across all six dimensions that decide whether it works',
      'See the failures static tools can’t — true text-over-media contrast, dead CTAs',
      'Output a prioritized list of gaps, not just a letter grade',
      'Wire the same engine into the sales motion as a before/after scorecard',
    ],
    implementation: [
      'Codified six weighted pillars as the single source of truth, with seven automated checks (accessibility, SEO, links, content, structured-data, performance, mobile) rolling up to a 0–100 score and an A–F grade',
      'Added a rendered (real-browser) mode that screenshots the page with and without text to measure true WCAG contrast, click-tests CTAs to catch buttons that do nothing, and renders at 390px to find overflow and sub-44px tap targets',
      'Measured per-image weight and format against explicit standards to flag oversized and legacy-format images',
      'Wired the engine into the /site-compare and /audit skills so a prospect’s legacy site and the proposed rebuild grade side by side',
    ],
    results: [
      'A repeatable, code-enforced website standard with a client-facing scorecard on demand',
      'Catches text-over-image contrast failures that pass every static checker',
      'Converts an audit into a numbered to-do list a prospect can act on',
      'Backs the sales pitch with a before/after grade instead of an opinion',
    ],
    metrics: [
      {
        label: 'Quality Pillars',
        value: '6',
        description: 'Weighted dimensions of a working site',
      },
      {
        label: 'Automated Checks',
        value: '7',
        description: 'Static + browser-rendered',
      },
      {
        label: 'Output',
        value: 'A–F',
        description: 'Grade on a 0–100 score, gaps as the deliverable',
      },
    ],
  },
  {
    slug: 'sierra-canyon-photo',
    title: 'Sierra Canyon Photography Competition',
    description:
      'A photography competition platform for Sierra Canyon School. Students submit photos, teachers moderate submissions, and students vote anonymously to select winners.',
    image: '/portfolio/sierra-canyon-photo.png',
    url: 'https://sierracanyon-photo.fuguelab.com',
    tags: [ProjectTag.FugueLab],
    serviceTypes: [ServiceType.WebDevelopment],
    clientTypes: [ClientType.School],
    tech: ['React', 'TypeScript', 'Tailwind CSS', 'AWS Lambda', 'DynamoDB', 'S3', 'Cognito', 'SES'],
    client: 'Sierra Canyon School',
    summary:
      'Sierra Canyon School needed a custom platform to run their photography competition with submission management, moderation workflows, and anonymous student voting.',
    objectives: [
      'Enable students to submit photos with metadata',
      'Provide teachers with moderation tools to approve/reject submissions',
      'Implement anonymous voting system with magic link authentication',
      'Support multiple competition phases (setup, submission, moderation, voting, closed)',
    ],
    implementation: [
      'Built a full-stack application with Vite + React frontend and AWS Lambda backend',
      'Designed single-table DynamoDB schema for competitions, submissions, and votes',
      'Implemented Cognito authentication for admins and magic link auth for student voters',
      'Created photo upload pipeline with S3 storage and CloudFront delivery',
      'Built admin dashboard for moderation and results viewing',
    ],
    results: [
      'Streamlined competition workflow from submission to winner announcement',
      'Enabled secure anonymous voting with magic link authentication',
      'Provided teachers with easy moderation and results management tools',
    ],
  },
]
